import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import {
  ASelect,
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import {
  EditOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { IModelDocument } from '../../shared/interfaces';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const ModelsDocuments: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>('');
  const [modelsDocuments, setModelsDocuments] = useState<IModelDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const isPrescriptionRoute = location.pathname.startsWith(
    '/models_prescriptions',
  );
  const kind =
    isPrescriptionRoute || searchParams.get('kind') === 'PRESCRIPTION'
      ? 'PRESCRIPTION'
      : 'GENERAL';
  const isPrescription = kind === 'PRESCRIPTION';

  const getListRoute = (value?: string) =>
    value === 'PRESCRIPTION' ? '/models_prescriptions' : '/models_documents';

  const getEditRoute = (id?: string, value?: string) =>
    value === 'PRESCRIPTION'
      ? `/model_prescription/${id}`
      : `/model_document/${id}`;

  const getCreateRoute = (value?: string) =>
    value === 'PRESCRIPTION'
      ? '/model_prescription/new'
      : '/model_document/new';

  const listModelsDocuments = useCallback(async () => {
    setLoading(true);
    setModelsDocuments([]);
    try {
      const response = await api.post('/documents/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
        kind,
      });
      setPages(response.data.count);
      setModelsDocuments(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('modelsDocumentsPage.toast.errorTitle'),
        description: t('modelsDocumentsPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, kind, t]);

  const handleKindChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value === 'PRESCRIPTION') {
      nextParams.set('kind', 'PRESCRIPTION');
    } else {
      nextParams.delete('kind');
    }

    setPage(1);
    setSearchParams(nextParams);
    navigate(getListRoute(value));
  };

  useEffect(() => {
    listModelsDocuments();
  }, [page, search, limit, listModelsDocuments]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('modelsDocumentsPage.intro.badge')}
        icon={<FileTextOutlined />}
        title={
          isPrescription
            ? t('modelsDocumentsPage.intro.titlePrescriptions')
            : t('modelsDocumentsPage.intro.titleDocuments')
        }
        description={
          isPrescription
            ? t('modelsDocumentsPage.intro.descriptionPrescriptions')
            : t('modelsDocumentsPage.intro.descriptionDocuments')
        }
      />
      <MenuOptions
        menu_items={[
          {
            label: t('modelsDocumentsPage.actions.refresh'),
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listModelsDocuments(),
          },
          permissionFind([PermissionActions.modelsDocumentsCreate])
            ? {
                label: isPrescription
                  ? t('modelsDocumentsPage.actions.createPrescription')
                  : t('modelsDocumentsPage.actions.create'),
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => navigate(getCreateRoute(kind)),
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <ASelect
          value={kind}
          onChange={handleKindChange}
          style={{ width: 220 }}
          options={[
            {
              label: t('modelsDocumentsPage.filters.documents'),
              value: 'GENERAL',
            },
            {
              label: t('modelsDocumentsPage.filters.prescriptions'),
              value: 'PRESCRIPTION',
            },
          ]}
        />
      </div>
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: t('modelsDocumentsPage.table.index'),
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: t('modelsDocumentsPage.table.title'),
              dataIndex: 'title',
            },
            {
              title: t('modelsDocumentsPage.table.status'),
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: t('modelsDocumentsPage.table.options'),
              width: 130,
              render: (cell: IModelDocument) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.modelsDocumentsUpdate])
                      ? {
                          label: t('modelsDocumentsPage.actions.edit'),
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            navigate(getEditRoute(cell.id, cell.kind || kind));
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={modelsDocuments}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default ModelsDocuments;
